import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

// The chosen variant + add-on names for an order line, so the invoice reprints the
// exact order even after the menu changes (the line stores its own names/prices).
function orderLineDetails(it) {
  return [it.variant_label, ...((it.addons || []).map(a => a && a.name))]
    .filter(Boolean).join(" · ");
}

export default function Invoice() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/invoice/${orderId}`)
      .then(({ data }) => setData(data))
      .catch(() => setError("Invoice not found"));
  }, [orderId]);

  const [searchParams] = useSearchParams();
  const printMode = searchParams.get("print") === "1";
  const signalledRef = useRef(false);

  // When the shared print helper (lib/print.js) loads this page in a hidden iframe it adds ?print=1.
  // The helper can't tell from the iframe's load event that the bill has rendered — React fetches the
  // invoice *after* the shell loads — so once the bill, or its not-found state, is actually on screen
  // we tell the opener and print this frame. Fires exactly once.
  useEffect(() => {
    if (!printMode || signalledRef.current) return;
    const post = (msg) => {
      try { window.parent.postMessage(msg, window.location.origin); } catch (_) {}
    };
    if (error) { signalledRef.current = true; post({ type: "tabon-print", ok: false }); return; }
    if (!data) return;                       // still loading — wait for the bill
    signalledRef.current = true;
    post({ type: "tabon-print", ok: true }); // bill is on screen — let the opener resolve now
    requestAnimationFrame(() => {
      // Measure across the BLOCKING window.print() and report it to the opener, which can't time the
      // call itself (it happens in this hidden frame). <400ms ≈ kiosk-silent, >1500ms ≈ a dialog was
      // dismissed (AH-1). A separate message so the opener's resolve/timeout above is untouched.
      const t0 = performance.now();
      let ok = true;
      try { window.print(); } catch (_) { ok = false; }
      post({ type: "tabon-print-timing", durationMs: performance.now() - t0, ok });
    });
  }, [printMode, data, error]);

  if (error) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", fontFamily: "sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <p style={{ color: "#888", fontSize: 14 }}>Invoice not found or has been cancelled.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff" }}>
        <p style={{ color: "#888", fontSize: 14, fontFamily: "sans-serif" }}>Loading…</p>
      </div>
    );
  }

  const { order, venue } = data;
  const isTaxInvoice = venue.receipt_type === "tax_invoice";
  // CGST + SGST come from the server already split from gst_amount (the odd paisa, if any, goes
  // on CGST, so the two re-add to gst_amount exactly). Fall back to a half split and its
  // complement for any pre-split payload, so cgst + sgst still equals gst_amount.
  const cgst = isTaxInvoice && order.gst_rate > 0 ? (order.cgst_amount ?? order.gst_amount / 2) : 0;
  const sgst = isTaxInvoice && order.gst_rate > 0 ? (order.sgst_amount ?? (order.gst_amount - cgst)) : 0;
  const docTitle = isTaxInvoice ? "Tax Invoice" : "Receipt";
  // Indian bills (AF-3): the document number is "Bill No." (the invoice number when allocated, else
  // the raw order/check number). On takeaway the order_number doubles as the "Token No." the counter
  // calls out. The channel + its location sit top-right opposite the date.
  const billNumber = order.invoice_number || order.order_number;
  const isTakeaway = order.order_type === "takeaway";
  const channelField = order.order_type === "room"
    ? `Room: ${order.room_number || order.table_number || "—"}`
    : order.order_type === "dine_in"
      ? `Dine In: ${order.table_number || "—"}`
      : "Takeaway";
  // A re-issued bill is stamped DUPLICATE, the way the KOT stamps ** REPRINT ** — the staff reprint
  // path loads the invoice with ?duplicate=1 (a customer's own receipt link never carries it).
  const duplicate = searchParams.get("duplicate") === "1";

  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh", padding: "24px 16px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      <style>{`
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .invoice-box { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>

      <div className="invoice-box" style={{
        background: "#fff", maxWidth: 680, margin: "0 auto",
        borderRadius: 8, boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}>

        {/* Re-issued bill stamp (AF-3), the counterpart to the KOT's ** REPRINT **. */}
        {duplicate && (
          <div style={{ background: "#1a1a1a", color: "#fff", textAlign: "center", padding: "6px 0", fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>
            Duplicate
          </div>
        )}

        {/* Header */}
        <div style={{ padding: "24px 28px 20px", borderBottom: "1px solid #eee" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1a1a1a", margin: 0 }}>{venue.name}</h1>
              <div style={{ fontSize: 12, color: "#888", marginTop: 6, lineHeight: 1.5 }}>
                {venue.address && <div>{venue.address}</div>}
                {venue.phone && <div>MOB: {venue.phone}</div>}
                {isTaxInvoice && venue.gstin && <div style={{ fontWeight: 600, color: "#444" }}>GST No: {venue.gstin}</div>}
                {isTaxInvoice && venue.fssai_number && <div style={{ fontWeight: 600, color: "#444" }}>FSSAI: {venue.fssai_number}</div>}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#888", fontWeight: 600 }}>{docTitle}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a", marginTop: 2 }}>Bill No. {billNumber}</div>
              {isTakeaway && <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginTop: 2 }}>Token No. {order.order_number}</div>}
              <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginTop: 4 }}>{channelField}</div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{formatDate(order.paid_at || order.created_at)}</div>
            </div>
          </div>
        </div>

        {/* Order meta */}
        <div style={{ padding: "14px 28px", borderBottom: "1px solid #eee", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, background: "#fafafa", fontSize: 12, color: "#666" }}>
          {/* Order # and the order type moved into the header (Bill No. + the channel field); this
              grid now carries the cashier, the party and who served, as Indian bills print (AF-3). */}
          {order.cashier_name && <div><span style={{ fontWeight: 600, color: "#444" }}>Cashier</span> {order.cashier_name}</div>}
          <div><span style={{ fontWeight: 600, color: "#444" }}>Customer</span> {order.customer_name}</div>
          {order.customer_phone && <div><span style={{ fontWeight: 600, color: "#444" }}>Phone</span> {order.customer_phone}</div>}
          {order.order_type === "dine_in" && order.covers && <div><span style={{ fontWeight: 600, color: "#444" }}>Persons</span> {order.covers}</div>}
          {order.vehicle_number && <div><span style={{ fontWeight: 600, color: "#444" }}>Vehicle</span> {order.vehicle_number}</div>}
          {/* Steward on the bill (AA-6) — only when one is attached. */}
          {order.steward_name && <div><span style={{ fontWeight: 600, color: "#444" }}>Served by</span> {order.steward_name}</div>}
        </div>

        {/* Items table */}
        <div style={{ padding: "0 28px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eee" }}>
                <th style={{ textAlign: "left", padding: "12px 0 8px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>No.</th>
                <th style={{ textAlign: "left", padding: "12px 0 8px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Item</th>
                <th style={{ textAlign: "center", padding: "12px 0 8px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Qty</th>
                <th style={{ textAlign: "right", padding: "12px 0 8px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Rate (₹)</th>
                <th style={{ textAlign: "right", padding: "12px 0 8px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Price (₹)</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "10px 0", color: "#9D00FF", fontWeight: 600 }}>{i + 1}.</td>
                  <td style={{ padding: "10px 0 10px 8px", color: "#1a1a1a" }}>
                    {it.name}
                    {orderLineDetails(it) && <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{orderLineDetails(it)}</div>}
                    {it.note && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Note: {it.note}</div>}
                  </td>
                  <td style={{ padding: "10px 0", textAlign: "center", color: "#444" }}>{it.qty}</td>
                  <td style={{ padding: "10px 0", textAlign: "right", color: "#444" }}>{(it.unit_price || 0).toFixed(2)}</td>
                  <td style={{ padding: "10px 0", textAlign: "right", fontWeight: 600, color: "#1a1a1a" }}>{(it.line_total || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ padding: "0 28px 24px" }}>
          <table style={{ width: "100%", fontSize: 13 }}>
            <tbody>
              <tr style={{ borderTop: "1px solid #eee" }}>
                <td style={{ padding: "10px 0 4px", color: "#666", fontWeight: 600 }}>Quantity</td>
                <td style={{ textAlign: "right", padding: "10px 0 4px", fontWeight: 600, color: "#444" }}>
                  {order.items.reduce((s, it) => s + it.qty, 0)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", color: "#666" }}>Sub Total</td>
                <td style={{ textAlign: "right", padding: "4px 0", color: "#444" }}>₹{(order.subtotal || order.total || 0).toFixed(2)}</td>
              </tr>
              {/* Discount / comped items and loyalty — present on dine-in checks, zero on orders */}
              {Number(order.discount_total) > 0 && (
                <tr>
                  <td style={{ padding: "4px 0", color: "#666" }}>Discount</td>
                  <td style={{ textAlign: "right", padding: "4px 0", color: "#444" }}>-₹{Number(order.discount_total).toFixed(2)}</td>
                </tr>
              )}
              {Number(order.loyalty_redemption) > 0 && (
                <tr>
                  <td style={{ padding: "4px 0", color: "#666" }}>Discount — Loyalty points</td>
                  <td style={{ textAlign: "right", padding: "4px 0", color: "#444" }}>-₹{Number(order.loyalty_redemption).toFixed(2)}</td>
                </tr>
              )}
              {isTaxInvoice && order.gst_rate > 0 && (
                <>
                  <tr>
                    <td style={{ padding: "4px 0", color: "#666" }}>CGST ({order.gst_rate / 2}%)</td>
                    <td style={{ textAlign: "right", padding: "4px 0", color: "#444" }}>₹{cgst.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "4px 0", color: "#666" }}>SGST ({order.gst_rate / 2}%)</td>
                    <td style={{ textAlign: "right", padding: "4px 0", color: "#444" }}>₹{sgst.toFixed(2)}</td>
                  </tr>
                </>
              )}
              {(order.charges || []).map((c, i) => (
                <tr key={`chg${i}`}>
                  <td style={{ padding: "4px 0", color: "#666" }}>{c.label || "Charge"}</td>
                  <td style={{ textAlign: "right", padding: "4px 0", color: "#444" }}>₹{(Number(c.amount) || 0).toFixed(2)}</td>
                </tr>
              ))}
              {order.round_off !== 0 && order.round_off !== null && order.round_off !== undefined && (
                <tr>
                  <td style={{ padding: "4px 0", color: "#666" }}>Round off</td>
                  <td style={{ textAlign: "right", padding: "4px 0", color: "#444" }}>
                    {order.round_off > 0 ? "+" : ""}₹{(order.round_off || 0).toFixed(2)}
                  </td>
                </tr>
              )}
              <tr style={{ borderTop: "2px solid #1a1a1a" }}>
                <td style={{ padding: "10px 0 0", fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>Total Payable Amount</td>
                <td style={{ textAlign: "right", padding: "10px 0 0", fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>
                  {/* Display the backend's rounded total as-is — it already sends bill.rounded_total
                      (K-3); re-rounding here client-side is the P0-3 anti-pattern and masked a wrong payload. */}
                  ₹{(order.total || 0).toFixed(2)}
                </td>
              </tr>
              {order.payment_method && (
                <tr>
                  <td style={{ padding: "4px 0 0", fontSize: 12, color: "#888" }}>Paid via</td>
                  <td style={{ textAlign: "right", padding: "4px 0 0", fontSize: 12, color: "#888", textTransform: "capitalize" }}>
                    {order.payment_method}{order.payment_reference ? ` · ${order.payment_reference}` : ""}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{ borderTop: "1px solid #eee", padding: "16px 28px", textAlign: "center", color: "#888", fontSize: 12 }}>
          Thank you for choosing us. Please visit again.
          <div style={{ marginTop: 6, fontSize: 11, color: "#bbb" }}>Powered by TabOn · tabon.app</div>
        </div>
      </div>

      {/* Print button — hidden on print */}
      <div className="no-print" style={{ maxWidth: 680, margin: "16px auto 0", textAlign: "right" }}>
        <button
          onClick={() => window.print()}
          style={{ background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          Print / Save PDF
        </button>
      </div>
    </div>
  );
}
